import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  BusinessOutlined,
  CheckCircle,
  EventBusyOutlined,
} from "@mui/icons-material";
import { AuthCard } from "../../components/auth";
import { INVITE_SECTION } from "./inviteSectionConfig";
import {
  getInviteHeroImageAlt,
  resolveInviteHeroImageSrc,
  type InviteHeroVariant,
} from "./inviteCompanyImage";
import type { InviteIntegrationSource } from "./inviteTypes";
import { inviteTextBoldSx, inviteTextSx } from "./inviteTypography";

const LOGO_SIZE = INVITE_SECTION.hero;

export interface InviteCardShellProps {
  companyImageS3Key?: string;
  companyImageUrl?: string;
  companyName?: string;
  integrationSource?: InviteIntegrationSource;
  heroVariant?: InviteHeroVariant;
  title: string;
  subtitle?: string | null;
  subtitleSlot?: React.ReactNode;
  linkedAccountSlot?: React.ReactNode;
  bodySlot?: React.ReactNode;
  errorSlot?: React.ReactNode;
  actionSlot?: React.ReactNode;
  isSkeleton?: boolean;
  showAccount?: boolean;
}

function renderHero(
  isSkeleton: boolean,
  heroVariant: InviteHeroVariant,
  heroImageSrc: string | null,
  heroImageAlt: string,
  companyName?: string,
) {
  if (isSkeleton) {
    return <Skeleton variant="rounded" width={LOGO_SIZE} height={LOGO_SIZE} />;
  }

  if (heroImageSrc) {
    return (
      <Box
        sx={{
          width: LOGO_SIZE,
          height: LOGO_SIZE,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <img
          src={heroImageSrc}
          alt={heroImageAlt}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>
    );
  }

  if (heroVariant === "inactive") {
    return (
      <EventBusyOutlined sx={{ fontSize: 56, color: "warning.main" }} />
    );
  }

  if (heroVariant === "error") {
    return <EventBusyOutlined sx={{ fontSize: 56, color: "error.main" }} />;
  }

  if (heroVariant === "success") {
    return <CheckCircle sx={{ fontSize: 64, color: "success.main" }} />;
  }

  const initial = companyName?.trim().charAt(0).toUpperCase();
  if (initial) {
    return (
      <Box
        sx={{
          width: LOGO_SIZE,
          height: LOGO_SIZE,
          borderRadius: 1,
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h4" sx={inviteTextBoldSx}>
          {initial}
        </Typography>
      </Box>
    );
  }

  return <BusinessOutlined sx={{ fontSize: 56, color: "text.secondary" }} />;
}

export const InviteCardShell: React.FC<InviteCardShellProps> = ({
  companyImageS3Key,
  companyImageUrl,
  companyName,
  integrationSource = "podscope",
  heroVariant: heroVariantProp,
  title,
  subtitle,
  subtitleSlot,
  linkedAccountSlot,
  bodySlot,
  errorSlot,
  actionSlot,
  isSkeleton = false,
  showAccount = false,
}) => {
  const heroVariant = heroVariantProp ?? "company";
  const heroImageSrc = resolveInviteHeroImageSrc({
    integrationSource,
    heroVariant,
    companyImageS3Key,
    companyImageUrl,
  });
  const heroImageAlt = getInviteHeroImageAlt({
    integrationSource,
    heroVariant,
    companyName,
  });

  return (
    <AuthCard
      layout="fluid"
      sx={{
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {renderHero(
          isSkeleton,
          heroVariant,
          heroImageSrc,
          heroImageAlt,
          companyName,
        )}
      </Box>

      <Box sx={{ px: 2 }}>
        {isSkeleton ? (
          <Skeleton variant="text" width="70%" height={40} sx={{ mx: "auto" }} />
        ) : (
          <Typography variant="h4" component="h1" sx={inviteTextBoldSx}>
            {title}
          </Typography>
        )}
      </Box>

      {(isSkeleton || subtitle || subtitleSlot) && (
        <Box sx={{ px: 2 }}>
          {isSkeleton ? (
            <Skeleton variant="text" width="85%" sx={{ mx: "auto" }} />
          ) : subtitleSlot ? (
            subtitleSlot
          ) : (
            <Typography
              variant="body1"
              align="center"
              sx={{ ...inviteTextSx, whiteSpace: "pre-line" }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {showAccount && linkedAccountSlot && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {linkedAccountSlot}
        </Box>
      )}

      {bodySlot}

      {errorSlot}

      {(isSkeleton || actionSlot) && (
        <Box sx={{ width: "100%" }}>
          {isSkeleton ? (
            <>
              <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={48} />
            </>
          ) : (
            actionSlot
          )}
        </Box>
      )}
    </AuthCard>
  );
};

export default InviteCardShell;
